import React, { useEffect, useState } from 'react';
import { Colors } from 'library';
import { addDays, startOfWeek, startOfDay, eachDayOfInterval, endOfDay, endOfWeek, isToday, isPast } from 'date-fns';
import { client, consoleLogInDev } from 'shared';
import { formatDateFromDateObject } from 'shared/Dates';
import HiredShift from './HiredShift';
import PostAShiftCard from './PostAShiftCard';
import UnfilledShifts from './UnfilledShifts';
import CalendarHeader from './CalendarHeader';
import CalendarFilters from './CalendarFilters';
import { useMediaQuery, styled } from '@mui/material';
import { Row, Stack, EmptyText } from 'library/ui';
import theme from 'theme';
import { BusinessSlot } from 'models';

interface IBusinessCalendarProps {
    addressId?: number;
    businessLocationId?: number;
    timezone?: string;
}

export interface ApplicantApprovalSummary {
    job: number;
    manual_clearinghouse_check?: boolean;
    applicants: {
        id: number;
        applied_user: {
            id: number;
            name: string;
            pictureUrl?: string;
        };
        job: number;
        viewed: boolean;
    }[];
}

export default function BusinessCalendar({ addressId, businessLocationId, timezone }: IBusinessCalendarProps) {
    const mdDown = useMediaQuery(theme.breakpoints.down('lg'));
    const [currentDay, setCurrentDay] = useState<Date>(new Date());
    const [showWeekend, setShowWeekend] = useState(false);
    const [hiredShifts, setHiredShifts] = useState<BusinessSlot[]>([]);
    const [unfilledShifts, setUnfilledShifts] = useState<{ [key: string]: BusinessSlot[] }>({});
    const [holidays, setHolidays] = useState<{ name: string; date: string }[]>([]);
    const [coveredShifts, setCoveredShifts] = useState<{ shift_count: number; date: string }[]>([]);
    const [applicantApprovals, setApplicantApprovals] = useState<ApplicantApprovalSummary[]>([]);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        setCurrentDay(mdDown ? new Date() : addDays(startOfWeek(new Date()), 1));
    }, [mdDown]);

    function weekDates(startDate: Date) {
        if (mdDown) return [startDate];
        const interval = {
            start: startDate,
            end: endOfWeek(startDate, { weekStartsOn: 1 }),
        };
        return eachDayOfInterval(interval);
    }

    async function jobsForCurrentInterval() {
        if (addressId) {
            try {
                setLoading(true);
                const interval = weekDates(currentDay);
                const body: { [key: string]: string } = {
                    address_id: addressId.toString(),
                    start: formatDateFromDateObject(interval[0]),
                    include_unfilled: 'true',
                };
                if (interval.length > 1) {
                    body['end'] = formatDateFromDateObject(interval.slice(-1)[0]);
                }
                const params = new URLSearchParams(body).toString();
                const result = await client(`api/business-calendar/calendar/?${params}`);
                setHiredShifts(
                    result.upcoming_shifts.filter(
                        (s: BusinessSlot) =>
                            s.appointments.length > 0 && s.appointments.some((appt) => appt.providers.length > 0),
                    ),
                );
                const unfilledShiftsList = result.upcoming_shifts.filter((s: BusinessSlot) => {
                    return s.appointments.length === 0 || s.appointments.some((appt) => appt.providers.length === 0);
                });
                setUnfilledShifts(unfilledShiftsByDate(unfilledShiftsList));
                setHolidays(result.holidays);
                setCoveredShifts(result.covered_shifts);
                getCalendarSummary(
                    Array.from(new Set(unfilledShiftsList.map((s: BusinessSlot) => s.ongoing_request.id))).join(','),
                    startOfDay(interval[0]),
                    endOfDay(interval.slice(-1)[0]),
                );
                setLoading(false);
            } catch (error) {
                consoleLogInDev(error);
            }
        }
    }

    async function getCalendarSummary(jobsList: string, start: Date, end: Date) {
        if (!jobsList) return;
        try {
            const params = new URLSearchParams({
                jobs: jobsList,
                start: start.toISOString(),
                end: end.toISOString(),
            });
            const result = await client(`api/applicant-approval/calendar-summary/?${params.toString()}`);
            setApplicantApprovals(result);
        } catch (error) {
            consoleLogInDev(error);
        }
    }

    useEffect(() => {
        jobsForCurrentInterval();
    }, [currentDay, addressId]);

    function unfilledShiftsByDate(slots: BusinessSlot[]) {
        return slots.reduce((p: { [key: string]: BusinessSlot[] }, c: BusinessSlot) => {
            const startDate = new Date(c.start_date).toLocaleDateString();
            const current = p[startDate] ?? [];
            return { ...p, [startDate]: [...current, c] };
        }, {});
    }

    function updateCurrentDay(d: Date) {
        setCurrentDay(mdDown ? d : addDays(startOfWeek(d), 1));
    }

    function hiredShiftsForDate(d: Date) {
        return hiredShifts.filter((s) => new Date(s.start_date).toLocaleDateString() === d.toLocaleDateString());
    }

    function CalendarColumns() {
        const dates = showWeekend || mdDown ? weekDates(currentDay) : weekDates(currentDay).slice(0, -2);
        const start = dates[0];
        const end = dates[dates.length - 1];
        return (
            <Row gap="none" justify="center" align="start" fullWidth>
                {dates.map((d) => (
                    <Column key={d.getDay()} d={d} end={end} start={start} mdDown={mdDown} gap="none" grow>
                        <CalendarHeader
                            d={d}
                            start={start}
                            end={end}
                            holidays={holidays}
                            coveredShifts={coveredShifts}
                        />
                        <ColumnBody>
                            {loading ? (
                                <EmptyText padding="md">Loading...</EmptyText>
                            ) : (
                                <Stack gap="sm" padding="sm">
                                    {unfilledShifts[d.toLocaleDateString()]?.length > 0 ? (
                                        <UnfilledShifts
                                            slots={unfilledShifts[d.toLocaleDateString()]}
                                            title={d.toLocaleDateString()}
                                            refresh={jobsForCurrentInterval}
                                            approvals={applicantApprovals}
                                        />
                                    ) : null}
                                    {hiredShiftsForDate(d).map((s) => (
                                        <HiredShift key={s.id} slot={s} refresh={jobsForCurrentInterval} />
                                    ))}
                                    <PostAShiftCard
                                        d={d}
                                        addressId={addressId}
                                        businessLocationId={businessLocationId}
                                        timezone={timezone}
                                        refresh={jobsForCurrentInterval}
                                    />
                                </Stack>
                            )}
                        </ColumnBody>
                    </Column>
                ))}
            </Row>
        );
    }

    return (
        <Stack gap="sm" fullWidth>
            <CalendarFilters
                currentDay={currentDay}
                setCurrentDay={updateCurrentDay}
                showWeekend={showWeekend}
                setShowWeekend={setShowWeekend}
            />
            <CalendarColumns />
        </Stack>
    );
}

const Column = styled(Stack)(({ d, end, start, mdDown }: { d: Date; end: Date; start: Date; mdDown: boolean }) => {
    const isPastDay = isPast(d) && !isToday(d);
    return {
        position: 'relative',
        height: 'calc(75vh + 75px)',
        border: `1px solid ${Colors.border}`,
        borderTopRightRadius: d.getDay() === end.getDay() || mdDown ? 18 : 0,
        borderBottomRightRadius: d.getDay() === end.getDay() || mdDown ? 18 : 0,
        borderTopLeftRadius: d.getDay() === start.getDay() || mdDown ? 18 : 0,
        borderBottomLeftRadius: d.getDay() === start.getDay() || mdDown ? 18 : 0,
        backgroundColor: Colors.white,
        overflow: 'hidden',
        ...(!isPastDay && {
            '&:hover': {
                backgroundColor: Colors.surface,
            },
            '&:hover .calendar-day-header': {
                backgroundColor: isToday(d) ? Colors.secondarySoft : Colors.surface,
            },
        }),
        ...(isPastDay && {
            '&::after': {
                content: '""',
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                backgroundColor: `${Colors.surface}70`,
            },
            '&:hover::after': {
                backgroundColor: 'transparent',
            },
        }),
    };
});

const ColumnBody = styled('div')({
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    paddingBottom: 20,
    width: '100%',
});
